import './pdfWorkerInit';
import '@enhances/with-resolvers';
import {CssBaseline, Box, CircularProgress, useMediaQuery, PaletteMode} from '@mui/material';
import {StyledEngineProvider, ThemeProvider} from '@mui/material/styles';
import * as Sentry from '@sentry/react';
import {Amplify} from 'aws-amplify';
import {ConsoleLogger} from 'aws-amplify/utils';
import React, {Suspense, useEffect, useMemo} from 'react';
import ReactDOM from 'react-dom/client';
import {BrowserRouter, useLocation, useNavigationType, createRoutesFromChildren, matchRoutes} from 'react-router-dom';

import {awsExports} from './amplify';
import {MixpanelProvider} from './analytics/MixpanelProvider';
import {captureFirstTouch} from './analytics/firstTouch';
import {App} from './App';
import {VITE_LOG_LEVEL, NODE_ENV} from './config';
import {ExtensionProvider} from './hooks/extension/ExtensionProvider';
import {reportWebVitals} from './reportWebVitals';
import {PreferencesProvider, usePreferences} from './hooks/preferences/PreferencesProvider';
import {buildTheme} from './theme';
import {beforeSend} from './utils/sentryFilter';
import {ErrorFallback} from './views/ErrorFallback';

import '@fontsource-variable/inter';

import './index.css';
import './i18n';

Amplify.configure(awsExports);
ConsoleLogger.LOG_LEVEL = VITE_LOG_LEVEL;
console.log('Amplify Config Loaded:', Amplify.getConfig());

if (NODE_ENV === 'production') {
  Sentry.init({
    dsn: 'https://6ffd39fd25904e7ca44b09fac179ac27@o70309.ingest.sentry.io/6374189',
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      /*new Replay({
        // This sets the sample rate to be 10%. You may want this to be 100% while
        // in development and sample at a lower rate in production
        sessionSampleRate: 0,
        // If the entire session is not sampled, use the below sample rate to sample
        // sessions when an error occurs.
        errorSampleRate: 1.0,
        // Mask all text content with asterisks (*). Passes text
        // content through to `maskTextFn` before sending to server.
        //
        // Defaults to true, uncomment to change
        maskAllText: false,
        // Block all media elements (img, svg, video, object,
        // picture, embed, map, audio)
        //
        // Defaults to true, uncomment to change
        // blockAllMedia: true,
      }),*/
    ],
    // We recommend adjusting this value in production, or using tracesSampler
    // for finer control
    tracesSampleRate: 0.0,
    allowUrls: [/app.perfectpost.fr/, /beta.perfectpost.fr/, /ngikijnhadhcmbfbimljlgimmocnhlpi/],
    ignoreErrors: [/Network Error/i, /Fetch Error/i, 'ResizeObserver loop limit exceeded'],
    // Écarte un refus métier attendu (quota, brouillon déjà supprimé…) qui aurait échappé à
    // son appelant, et une erreur levée dans une extension tierce (Similarweb qui remplace
    // `fetch`…) sans cadre de l'extension PerfectPost — et RIEN d'autre : ni 5xx, ni 4xx sans
    // code connu, ni coupure réseau ordinaire (cf. `sentryFilter.ts`).
    beforeSend,
  });
}

const isPrefersColorSchemeSupported = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }

  try {
    return window.matchMedia('(prefers-color-scheme: dark)').media !== 'not all';
  } catch (error) {
    return false;
  }
};

function Main() {
  // Le mode d'apparence vient des PRÉFÉRENCES LOCALES et non de `GET /me` : le
  // thème doit être choisi au tout premier rendu, avant qu'aucune requête n'ait
  // abouti — c'est ce que `redux-persist` permettait. `SessionProvider` y
  // recopie la valeur du serveur dès qu'elle arrive.
  const {appearanceMode: userAppearanceMode, hydrated} = usePreferences();
  const normalizedAppearanceMode: 'system' | 'light' | 'dark' =
    userAppearanceMode === 'dark' || userAppearanceMode === 'light' ? userAppearanceMode : 'system';
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)', {defaultMatches: false});
  const hasColorSchemeSupport = useMemo(() => isPrefersColorSchemeSupported(), []);
  const mode: PaletteMode = useMemo(() => {
    if (normalizedAppearanceMode === 'dark' || normalizedAppearanceMode === 'light') {
      return normalizedAppearanceMode;
    }

    return hasColorSchemeSupport && prefersDark ? 'dark' : 'light';
  }, [hasColorSchemeSupport, prefersDark, normalizedAppearanceMode]);
  const theme = useMemo(() => buildTheme(mode), [mode]);

  useEffect(() => {
    const defaultThemeMeta = document.querySelector('meta[name="theme-color"]:not([media])');
    const prefersMeta = document.querySelector('meta[name="theme-color"][media]');
    const themeBackground = theme.palette.background.default;

    if (defaultThemeMeta instanceof HTMLMetaElement) {
      defaultThemeMeta.content = themeBackground;
    }

    if (prefersMeta instanceof HTMLMetaElement) {
      if (normalizedAppearanceMode === 'system') {
        prefersMeta.content = '#000000';
        prefersMeta.media = '(prefers-color-scheme: dark)';
      } else if (normalizedAppearanceMode === 'dark') {
        prefersMeta.content = themeBackground;
        prefersMeta.media = 'all';
      } else {
        prefersMeta.content = themeBackground;
        prefersMeta.media = 'not all';
      }
    }
  }, [theme, normalizedAppearanceMode]);

  // Rien avant que les préférences ne soient relues, comme le faisait
  // `<PersistGate loading={null}>` : sinon le thème sombre s'appliquerait après
  // un éclair de thème clair.
  if (!hydrated) {
    return null;
  }

  return (
    <ThemeProvider theme={theme}>
      <Suspense
        fallback={
          <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>
            <CircularProgress />
          </Box>
        }
      >
        <MixpanelProvider>
          <BrowserRouter>
            <CssBaseline />
            <App />
          </BrowserRouter>
        </MixpanelProvider>
      </Suspense>
    </ThemeProvider>
  );
}
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
// Avant tout rendu (et donc avant toute redirection interne qui perdrait les UTM de l'URL d'arrivée).
captureFirstTouch();

const root = ReactDOM.createRoot(document.getElementById('root')!);
root.render(
  // `PreferencesProvider` remplace le couple `Provider` + `PersistGate` : c'est
  // lui qui relit les préférences persistées au démarrage. Il n'attend rien pour
  // rendre — c'est `Main` qui retarde le premier rendu le temps de l'hydratation,
  // et seulement pour éviter un éclair de thème clair.
  <PreferencesProvider>
    <ExtensionProvider>
      <StyledEngineProvider injectFirst>
        <Sentry.ErrorBoundary
          fallback={({error, resetError}) => <ErrorFallback error={error as Error} resetError={resetError} />}
          showDialog={false}
        >
          <Main />
        </Sentry.ErrorBoundary>
      </StyledEngineProvider>
    </ExtensionProvider>
  </PreferencesProvider>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals(console.log);
